import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { CalendarCheck, Send, Trash2, BellRing, MapPin, Plus, Users, MailCheck, Pencil, Eye, GraduationCap, ClipboardCheck, Video, FileText, Paperclip, NotebookText, BookOpen } from "lucide-react";
import ConvocationSummaryPanel from "@/components/ConvocationSummaryPanel";
import CheckinQrButton from "@/components/CheckinQrButton";
import { SessionRoom } from "@/components/SessionRoom";
import StudyPanel from "@/components/StudyPanel";

const CLUBS = ["Diplomatas", "Analistas", "Sentinelas", "Exploradores"];
const CLUB_EMOJI = { Analistas: "🧠", Diplomatas: "🤝", Sentinelas: "🛡️", Exploradores: "🚀" };
const GROUPS = [{ key: "staff", label: "Staff da Liga" }, { key: "coaches", label: "Secretários (contas de secretário)" }];
const STATUSES = [
  { key: "competing", label: "A coopetir" },
  { key: "observer", label: "Observadores" },
];
const ANSWER = {
  yes: { label: "Confirmou", color: "#22C55E" },
  maybe: { label: "Talvez", color: "#EAB308" },
  no: { label: "Não pode", color: "#737373" },
  pending: { label: "Sem resposta", color: "#404040" },
};
const fmt = (iso) => iso ? new Date(iso).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";

const EMPTY_TRAINING = {
  kind: "training",
  title: "",
  location: "Market Center do Restelo",
  date: new Date().toISOString().slice(0, 10),
  notes: "",
  importance: "",
  online_link: "",
  material_url: "",
  trainer_user_id: "",
  duration_minutes: 90,
  club_times: {},
  group_times: {},
  statuses: [],
  include_badges: true,
};

const EMPTY = {
  kind: "meeting",
  trainer_user_id: "",
  importance: "",
  online_link: "",
  material_url: "",
  title: "Reunião de Clube · Seleção dos 32",
  location: "Market Center do Restelo",
  date: "2026-10-08",
  notes: "Confirma a tua presença. Vamos fechar os planteis e apresentar a edição.",
  duration_minutes: 60,
  club_times: { Diplomatas: "11:00", Analistas: "12:00", Sentinelas: "14:00", Exploradores: "15:00" },
  group_times: {},
  statuses: [],
  include_badges: true,
};

export default function Convocations() {
  const [rows, setRows] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // null | "new" | id
  const [form, setForm] = useState(EMPTY);
  const [preview, setPreview] = useState(null);
  const [trainers, setTrainers] = useState([]);
  const [attendance, setAttendance] = useState(null);
  const [summaryId, setSummaryId] = useState(null);
  const [studyId, setStudyId] = useState(null);
  const [singleTime, setSingleTime] = useState("11:00");
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/convocations").then((r) => setRows(r.data || []))
    .catch((err) => toast.error(formatApiError(err)));
  useEffect(() => {
    load();
    api.get("/convocations/meta/trainers").then((r) => setTrainers(r.data || [])).catch(() => {});
  }, []);

  const startNew = (kind) => {
    setForm(kind === "training" ? EMPTY_TRAINING : EMPTY);
    setEditing("new");
    setPreview(null);
  };
  const startEdit = (c) => {
    setForm({
      title: c.title, location: c.location, date: c.date, notes: c.notes || "",
      kind: c.kind || "meeting", trainer_user_id: c.trainer_user_id || "",
      importance: c.importance || "", online_link: c.online_link || "",
      material_url: c.material_url || "",
      duration_minutes: c.duration_minutes || 60,
      club_times: c.club_times || {}, group_times: c.group_times || {},
      statuses: c.statuses || [], include_badges: c.include_badges !== false,
    });
    setEditing(c.id);
    setPreview(null);
  };

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        title: form.title, location: form.location, date: form.date, notes: form.notes,
        duration_minutes: Number(form.duration_minutes) || 60,
        club_times: form.club_times, group_times: form.group_times,
        statuses: form.statuses, include_badges: form.include_badges, reminder_hours: 24,
        kind: form.kind || "meeting", trainer_user_id: form.trainer_user_id || null,
        importance: form.importance, online_link: form.online_link, material_url: form.material_url,
      };
      if (editing === "new") await api.post("/convocations", body);
      else await api.patch(`/convocations/${editing}`, body);
      toast.success(editing === "new" ? "Convocatória criada. Revê os destinatários e envia." : "Convocatória actualizada");
      setEditing(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  const act = async (id, path, label) => {
    setBusy(true);
    try {
      const r = await api.post(`/convocations/${id}/${path}`);
      const d = r.data || {};
      if (path === "remind") {
        toast.success(
          `Lembrete só para quem não respondeu: ${d.emails} emails · ${d.pushes ?? 0} notificações. ` +
          `${d.skipped ?? 0} já tinham respondido (sim, talvez ou não) e não receberam nada.`
        );
      } else {
        toast.success(`${label}: ${d.recipients ?? d.emails} destinatários · ${d.emails} emails · ${d.pushes ?? 0} notificações`);
      }
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  const showPreview = async (id) => {
    try {
      const r = await api.get(`/convocations/${id}/recipients`);
      setPreview({ id, ...r.data });
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const testSend = async (id) => {
    const email = window.prompt("Enviar o email de teste para que endereço?", "");
    if (!email) return;
    setBusy(true);
    try {
      const r = await api.post(`/convocations/${id}/test`, { email });
      toast.success(`Teste enviado para ${r.data.email} (${r.data.club} · ${r.data.time})`);
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  const openAttendance = async (id) => {
    try {
      const r = await api.get(`/convocations/${id}/attendance`);
      setAttendance({ id, ...r.data, selected: new Set(r.data.people.filter((p) => p.present).map((p) => p.ref)),
        late: new Set(r.data.people.filter((p) => p.late).map((p) => p.ref)) });
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const togglePresent = (ref) => {
    setAttendance((a) => {
      const sel = new Set(a.selected);
      if (sel.has(ref)) sel.delete(ref); else sel.add(ref);
      return { ...a, selected: sel };
    });
  };

  const toggleLate = (ref) => {
    setAttendance((a) => {
      const late = new Set(a.late);
      if (late.has(ref)) late.delete(ref); else late.add(ref);
      return { ...a, late };
    });
  };

  const saveAttendance = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/convocations/${attendance.id}/attendance`, {
        present: Array.from(attendance.selected),
        late: Array.from(attendance.late || []).filter((ref) => attendance.selected.has(ref)),
      });
      toast.success(attendance.convocation?.kind === "training"
        ? `${r.data.present} presentes · ${r.data.pontos_atribuidos} ganharam os 8 pontos`
        : `${r.data.present} presentes · presenças e pontualidade atualizadas nos pontos`);
      setAttendance(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  const uploadMaterial = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads/material", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, material_url: r.data.url, material_name: r.data.filename }));
      toast.success(`${r.data.filename} anexado`);
    } catch (err) { toast.error(formatApiError(err)); }
    setUploading(false);
    e.target.value = "";
  };

  const remove = async (id) => {
    try { await api.delete(`/convocations/${id}`); toast.success("Convocatória apagada"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const toggleClub = (c) => {
    const next = { ...form.club_times };
    if (next[c]) delete next[c]; else next[c] = "11:00";
    setForm({ ...form, club_times: next });
  };
  const toggleGroup = (k) => {
    const next = { ...form.group_times };
    if (next[k]) delete next[k]; else next[k] = "10:30";
    setForm({ ...form, group_times: next });
  };
  const toggleStatus = (k) => {
    const has = form.statuses.includes(k);
    setForm({ ...form, statuses: has ? form.statuses.filter((x) => x !== k) : [...form.statuses, k] });
  };

  if (!rows) return <div className="kw-overline">A carregar…</div>;

  const audienceLabel = (c) => {
    const bits = [];
    CLUBS.filter((x) => c.club_times?.[x]).forEach((x) => bits.push(`${x} ${c.club_times[x]}`));
    GROUPS.filter((g) => c.group_times?.[g.key]).forEach((g) => bits.push(`${g.label.split(" (")[0]} ${c.group_times[g.key]}`));
    return bits.join(" · ");
  };

  return (
    <div className="space-y-6 kw-fade-up" data-testid="convocations-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="kw-overline">Comunicação com os coopetidores</div>
          <h1 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tighter">
            Convo<span className="text-[#E50000]">catórias</span>
          </h1>
          <p className="text-sm text-[#A3A3A3] mt-2 max-w-2xl">
            Escolhe quem recebe — clube a clube, por estado na liga, com ou sem cargos, e os grupos
            transversais de staff e coaches. Cada pessoa recebe a hora do seu grupo, confirma num clique
            e mete a reunião na agenda. Lembrete automático 24h antes a quem não responder.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => startNew("meeting")} className="kw-btn-secondary inline-flex items-center gap-2" data-testid="convocation-new">
            <Plus className="w-4 h-4" /> Nova reunião
          </button>
          <button onClick={() => startNew("training")} className="kw-btn-primary inline-flex items-center gap-2" data-testid="training-new">
            <GraduationCap className="w-4 h-4" /> Nova formação
          </button>
        </div>
      </div>

      {editing && (
        <div className="kw-card p-6 space-y-5" data-testid="convocation-form">
          <div className="flex gap-2">
            {[{ k: "meeting", label: "Reunião" }, { k: "training", label: "Formação KW" }].map((t) => (
              <button key={t.k} onClick={() => setForm({ ...form, kind: t.k })}
                className={`px-4 py-2 text-[10px] uppercase tracking-widest font-bold border ${form.kind === t.k ? "bg-[#E50000] border-[#E50000] text-white" : "border-[#333] text-[#A3A3A3]"}`}
                data-testid={`convocation-kind-${t.k}`}>
                {t.label}
              </button>
            ))}
          </div>
          {form.kind === "training" && (
            <div className="grid md:grid-cols-2 gap-4 border border-[#222] p-4">
              <div>
                <label className="kw-overline block mb-2">Formador (faz a chamada)</label>
                <select className="kw-input" value={form.trainer_user_id}
                  onChange={(e) => setForm({ ...form, trainer_user_id: e.target.value })}
                  data-testid="convocation-trainer">
                  <option value="">Escolher formador…</option>
                  {trainers.map((t) => (
                    <option key={t.id} value={t.id}>{t.name} · {t.role === "coach" ? "coach" : "staff"}{t.club ? ` · ${t.club}` : ""}</option>
                  ))}
                </select>
                <p className="text-[10px] uppercase tracking-widest text-[#737373] mt-2">
                  marca presenças e os 8 pontos entram sozinhos
                </p>
              </div>
              <div>
                <label className="kw-overline block mb-2">Porque importa esta formação</label>
                <input className="kw-input" value={form.importance} placeholder="O que levam desta sessão"
                  onChange={(e) => setForm({ ...form, importance: e.target.value })} data-testid="convocation-importance" />
              </div>
              <div>
                <label className="kw-overline block mb-2">Link Zoom / Meet (se for online)</label>
                <input className="kw-input" value={form.online_link} placeholder="https://"
                  onChange={(e) => setForm({ ...form, online_link: e.target.value })} data-testid="convocation-online" />
              </div>
              <div>
                <label className="kw-overline block mb-2">Material de apoio (PDF, PPT ou imagem)</label>
                <div className="flex gap-2">
                  <input className="kw-input flex-1" value={form.material_url} placeholder="https:// ou anexa um ficheiro"
                    onChange={(e) => setForm({ ...form, material_url: e.target.value })} data-testid="convocation-material" />
                  <label className="kw-btn-secondary !py-2 text-[10px] inline-flex items-center gap-1.5 cursor-pointer shrink-0">
                    <Paperclip className="w-3 h-3" /> {uploading ? "a enviar…" : "Anexar"}
                    <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp,.pptx,.docx,.csv,.txt"
                      onChange={uploadMaterial} data-testid="convocation-material-file" />
                  </label>
                </div>
                {form.material_name && (
                  <p className="text-[10px] uppercase tracking-widest text-[#22C55E] mt-2">
                    anexado: {form.material_name}
                  </p>
                )}
              </div>
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="kw-overline block mb-2">Título</label>
              <input className="kw-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                data-testid="convocation-title" />
            </div>
            <div>
              <label className="kw-overline block mb-2">Local</label>
              <input className="kw-input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
                data-testid="convocation-location" />
            </div>
            <div>
              <label className="kw-overline block mb-2">Data</label>
              <input type="date" className="kw-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                data-testid="convocation-date" />
            </div>
          </div>

          <div>
            <div className="flex flex-wrap items-center gap-3 mb-2">
              <div className="kw-overline flex-1">Quem recebe · clubes e horas</div>
              <button onClick={() => {
                const t = singleTime || "11:00";
                setForm({ ...form, club_times: Object.fromEntries(CLUBS.map((c) => [c, t])) });
              }}
                className="text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 border border-[#E50000] text-[#E50000] hover:bg-[#E50000] hover:text-white"
                data-testid="convocation-all-clubs">
                Todos os consultores
              </button>
              <button onClick={() => setForm({ ...form, club_times: {} })}
                className="text-[10px] uppercase tracking-widest text-[#737373] hover:text-white"
                data-testid="convocation-clear-clubs">
                limpar
              </button>
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">Hora para todos</span>
                <input type="time" className="kw-input !py-1.5 text-xs w-28" value={singleTime}
                  onChange={(e) => setSingleTime(e.target.value)} data-testid="convocation-single-time" />
                <button onClick={() => {
                  if (!singleTime) return;
                  const ct = Object.fromEntries(Object.keys(form.club_times).map((c) => [c, singleTime]));
                  const gt = Object.fromEntries(Object.keys(form.group_times).map((g) => [g, singleTime]));
                  setForm({ ...form, club_times: ct, group_times: gt });
                }}
                  className="text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 border border-[#333] text-[#A3A3A3] hover:text-white"
                  data-testid="convocation-apply-time">
                  aplicar
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2">
              {CLUBS.map((c) => {
                const on = !!form.club_times[c];
                return (
                  <div key={c} className={`border p-3 ${on ? "border-[#E50000]" : "border-[#222]"}`}>
                    <button onClick={() => toggleClub(c)} className="flex items-center gap-2 w-full text-left"
                      data-testid={`convocation-club-${c}`}>
                      <span className={`w-3 h-3 border ${on ? "bg-[#E50000] border-[#E50000]" : "border-[#444]"}`} />
                      <span>{CLUB_EMOJI[c]}</span>
                      <span className="text-sm font-bold flex-1">{c}</span>
                    </button>
                    {on && (
                      <input type="time" className="kw-input !py-1.5 mt-2 text-xs" value={form.club_times[c]}
                        onChange={(e) => setForm({ ...form, club_times: { ...form.club_times, [c]: e.target.value } })}
                        data-testid={`convocation-time-${c}`} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <div className="kw-overline mb-2">Dentro dos clubes, quem recebe</div>
              <div className="flex flex-wrap gap-2">
                {STATUSES.map((s) => {
                  const on = form.statuses.includes(s.key);
                  return (
                    <button key={s.key} onClick={() => toggleStatus(s.key)}
                      className={`px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold border ${on ? "bg-[#E50000] border-[#E50000] text-white" : "border-[#333] text-[#A3A3A3]"}`}
                      data-testid={`convocation-status-${s.key}`}>
                      {s.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-[10px] uppercase tracking-widest text-[#737373] mt-2">
                {form.statuses.length === 0 ? "nenhum selecionado = todos os consultores do clube" : "só os estados selecionados"}
              </p>
              <button onClick={() => setForm({ ...form, include_badges: !form.include_badges })}
                className="mt-3 flex items-center gap-2 text-left" data-testid="convocation-badges">
                <span className={`w-3 h-3 border ${form.include_badges ? "bg-[#EAB308] border-[#EAB308]" : "border-[#444]"}`} />
                <span className="text-xs">Incluir quem tem cargo — o secretário e o presidente entram com o clube que acompanham</span>
              </button>
            </div>

            <div>
              <div className="kw-overline mb-2">Grupos transversais</div>
              <div className="space-y-2">
                {GROUPS.map((g) => {
                  const on = !!form.group_times[g.key];
                  return (
                    <div key={g.key} className={`border p-3 flex items-center gap-3 ${on ? "border-[#E50000]" : "border-[#222]"}`}>
                      <button onClick={() => toggleGroup(g.key)} className="flex items-center gap-2 flex-1 text-left"
                        data-testid={`convocation-group-${g.key}`}>
                        <span className={`w-3 h-3 border ${on ? "bg-[#E50000] border-[#E50000]" : "border-[#444]"}`} />
                        <span className="text-sm">{g.label}</span>
                      </button>
                      {on && (
                        <input type="time" className="kw-input !py-1.5 text-xs w-28" value={form.group_times[g.key]}
                          onChange={(e) => setForm({ ...form, group_times: { ...form.group_times, [g.key]: e.target.value } })}
                          data-testid={`convocation-grouptime-${g.key}`} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="kw-overline block mb-2">Mensagem (opcional)</label>
              <textarea className="kw-input min-h-[90px]" value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="convocation-notes" />
            </div>
            <div>
              <label className="kw-overline block mb-2">Duração (minutos)</label>
              <input type="number" className="kw-input" value={form.duration_minutes}
                onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} data-testid="convocation-duration" />
            </div>
          </div>

          <div className="flex gap-3">
            <button className="kw-btn-primary" onClick={save} disabled={busy} data-testid="convocation-save">
              {editing === "new" ? "Criar convocatória" : "Guardar alterações"}
            </button>
            <button className="kw-btn-secondary" onClick={() => setEditing(null)}>Cancelar</button>
          </div>
        </div>
      )}

      {rows.length === 0 ? (
        <div className="kw-card p-10 text-center text-[#737373] text-sm" data-testid="convocations-empty">
          Ainda não há convocatórias. Cria a primeira e escolhe quem recebe.
        </div>
      ) : rows.map((c) => {
        const isOpen = openId === c.id;
        return (
          <div key={c.id} className="kw-card p-0" data-testid={`convocation-${c.id}`}>
            <div className="p-5 flex flex-wrap items-start gap-4">
              <CalendarCheck className="w-5 h-5 text-[#E50000] mt-1 shrink-0" />
              <button onClick={() => setOpenId(isOpen ? null : c.id)} className="flex-1 text-left min-w-[240px]">
                <div className="flex items-center gap-2 flex-wrap">
                  {c.kind === "training" && (
                    <span className="text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 bg-[#EAB308] text-black inline-flex items-center gap-1">
                      <GraduationCap className="w-3 h-3" /> Formação · 8 pts
                    </span>
                  )}
                  <div className="font-display text-xl font-black uppercase tracking-tight">{c.title}</div>
                </div>
                {c.kind === "training" && (
                  <div className="text-[10px] uppercase tracking-widest text-[#EAB308] mt-1 flex flex-wrap gap-2">
                    {c.trainer_name && <span>formador: {c.trainer_name}</span>}
                    {c.online_link && <span className="inline-flex items-center gap-1"><Video className="w-3 h-3" /> online</span>}
                    {c.material_url && <span className="inline-flex items-center gap-1"><FileText className="w-3 h-3" /> material</span>}
                  </div>
                )}
                <div className="text-xs text-[#A3A3A3] mt-1 flex items-center gap-2 flex-wrap">
                  <MapPin className="w-3 h-3" /> {c.location}
                  <span className="text-[#555]">·</span>
                  {new Date(`${c.date}T12:00:00`).toLocaleDateString("pt-PT", { weekday: "long", day: "2-digit", month: "long" })}
                  <span className="text-[#555]">·</span>
                  {audienceLabel(c)}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-[#737373] mt-1">
                  {(c.statuses?.length ? c.statuses.map((s) => STATUSES.find((x) => x.key === s)?.label).join(" + ") : "todos os consultores")}
                  {c.include_badges === false && " · sem cargos"}
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {["yes", "maybe", "no", "pending"].map((k) => (
                    <span key={k} className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 border"
                      style={{ borderColor: ANSWER[k].color, color: ANSWER[k].color }}
                      data-testid={`convocation-tally-${k}-${c.id}`}>
                      {ANSWER[k].label} {c.tally?.[k] ?? 0}
                    </span>
                  ))}
                  <span className="text-[10px] uppercase tracking-widest text-[#737373] px-2 py-0.5">
                    {c.status === "sent" ? `enviada ${fmt(c.sent_at)}` : "rascunho"}
                    {c.reminder_sent_at && ` · lembrete ${fmt(c.reminder_sent_at)}`}
                  </span>
                </div>
              </button>
              <div className="flex flex-wrap gap-2 shrink-0">
                <button onClick={() => startEdit(c)} className="kw-btn-secondary !py-2 text-[10px] inline-flex items-center gap-1.5"
                  data-testid={`convocation-edit-${c.id}`}>
                  <Pencil className="w-3 h-3" /> Editar
                </button>
                <button onClick={() => showPreview(c.id)} className="kw-btn-secondary !py-2 text-[10px] inline-flex items-center gap-1.5"
                  data-testid={`convocation-preview-${c.id}`}>
                  <Eye className="w-3 h-3" /> Ver destinatários
                </button>
                <button onClick={() => testSend(c.id)} disabled={busy} className="kw-btn-secondary !py-2 text-[10px] inline-flex items-center gap-1.5"
                  data-testid={`convocation-test-${c.id}`}>
                  <MailCheck className="w-3 h-3" /> Teste
                </button>
                <button onClick={() => act(c.id, "send", "Enviada")} disabled={busy} className="kw-btn-primary !py-2 text-[10px] inline-flex items-center gap-1.5"
                  data-testid={`convocation-send-${c.id}`}>
                  <Send className="w-3 h-3" /> {c.status === "sent" ? "Reenviar" : "Enviar"}
                </button>
                {c.status === "sent" && (
                  <button onClick={() => act(c.id, "remind", "Lembrete")} disabled={busy} className="kw-btn-secondary !py-2 text-[10px] inline-flex items-center gap-1.5"
                    title="Envia só a quem ainda não respondeu. Quem confirmou, disse talvez ou não pode, não recebe nada."
                    data-testid={`convocation-remind-${c.id}`}>
                    <BellRing className="w-3 h-3" /> Lembrar quem falta ({c.tally?.pending ?? 0})
                  </button>
                )}
                {c.status === "sent" && (
                  <button onClick={() => openAttendance(c.id)} className="kw-btn-secondary !py-2 text-[10px] inline-flex items-center gap-1.5 !border-[#EAB308] !text-[#EAB308]"
                    data-testid={`convocation-attendance-${c.id}`}>
                    <ClipboardCheck className="w-3 h-3" /> Chamada de presenças
                  </button>
                )}
                {c.status === "sent" && <CheckinQrButton conv={c} />}
                {c.status !== "cancelled" && <SessionRoom s={c} onChange={load} />}
                {c.status === "sent" && (
                  <button onClick={() => setStudyId(studyId === c.id ? null : c.id)}
                    className="kw-btn-secondary !py-2 text-[10px] inline-flex items-center gap-1.5"
                    data-testid={`convocation-study-${c.id}`}>
                    <BookOpen className="w-3 h-3" /> Estudo prévio {c.study_files?.length ? `(${c.study_files.length})` : ""}
                  </button>
                )}
                {c.status === "sent" && (
                  <button onClick={() => setSummaryId(summaryId === c.id ? null : c.id)}
                    className="kw-btn-secondary !py-2 text-[10px] inline-flex items-center gap-1.5"
                    data-testid={`convocation-summary-${c.id}`}>
                    <NotebookText className="w-3 h-3" /> Resumo {c.summary_sent_at ? "· enviado" : ""}
                  </button>
                )}
                <button onClick={() => remove(c.id)} className="text-[#555] hover:text-[#E50000] p-2"
                  data-testid={`convocation-del-${c.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            {preview?.id === c.id && (
              <div className="border-t border-[#161616] p-5" data-testid={`convocation-preview-box-${c.id}`}>
                <div className="kw-overline mb-3">Vão receber · {preview.total} pessoas</div>
                <div className="flex flex-wrap gap-2">
                  {preview.groups.map((g) => (
                    <span key={g.group} className="text-xs border border-[#333] px-3 py-1.5">
                      <b>{g.group}</b> {g.time} · {g.count}
                      {g.no_email > 0 && <span className="text-[#EAB308]"> · {g.no_email} sem email</span>}
                    </span>
                  ))}
                </div>
                {preview.without_email?.length > 0 && (
                  <p className="text-[10px] uppercase tracking-widest text-[#737373] mt-3">
                    sem email: {preview.without_email.join(", ")}
                  </p>
                )}
              </div>
            )}

            {summaryId === c.id && <ConvocationSummaryPanel conv={c} onDone={load} />}
            {studyId === c.id && <StudyPanel conv={c} onDone={load} />}

            {attendance?.id === c.id && (
              <div className="border-t border-[#161616] p-5" data-testid={`attendance-box-${c.id}`}>
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <ClipboardCheck className="w-4 h-4 text-[#EAB308]" />
                  <div className="kw-overline flex-1">
                    Chamada de presenças · {attendance.selected.size} de {attendance.people.length}
                  </div>
                  <button onClick={saveAttendance} disabled={busy} className="kw-btn-primary !py-2 text-[10px]"
                    data-testid="attendance-save">
                    {c.kind === "training" ? "Guardar e atribuir 8 pontos" : "Guardar presenças"}
                  </button>
                  <button onClick={() => setAttendance(null)} className="text-[10px] uppercase tracking-widest text-[#737373] hover:text-white">
                    fechar
                  </button>
                </div>
                <p className="text-xs text-[#A3A3A3] mb-4 max-w-2xl">
                  {c.kind === "training" ? <>
                  Marca quem esteve presente — mesmo quem não respondeu à convocatória. Os 8 pontos entram
                  no registo semanal de cada um e o consultor já não os pode somar outra vez. Se desmarcares
                  alguém, os pontos saem.</> : <>
                  Marca quem esteve presente e carrega em "Pontual/Atrasado" para corrigir a pontualidade (+5 só a quem chegou à hora).
                  Quem leu o QR já aparece marcado. No fim da reunião, quem não leu o QR fica com falta — podes corrigir aqui.</>}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-1">
                  {attendance.people.map((p) => {
                    const on = attendance.selected.has(p.ref);
                    const isLate = on && attendance.late?.has(p.ref);
                    const meeting = c.kind !== "training";
                    return (
                      <div key={p.ref} className={`flex items-center border transition-colors ${on ? (isLate ? "border-[#EAB308] bg-[#EAB308]/10" : "border-[#22C55E] bg-[#22C55E]/10") : "border-[#222] hover:border-[#444]"}`}>
                        <button onClick={() => togglePresent(p.ref)} className="flex items-center gap-2 px-3 py-2 text-left flex-1 min-w-0"
                          data-testid={`attendance-${p.ref}`}>
                          <span className={`w-3 h-3 border shrink-0 ${on ? "bg-[#22C55E] border-[#22C55E]" : "border-[#444]"}`} />
                          <span className="text-xs flex-1 truncate">{p.name}{p.source === "qr" && <span className="text-[9px] text-[#737373]"> · QR</span>}</span>
                          <span className="text-[9px] uppercase tracking-widest shrink-0"
                            style={{ color: ANSWER[p.answer || "pending"].color }}>
                            {p.club}
                          </span>
                        </button>
                        {meeting && on && (
                          <button onClick={() => toggleLate(p.ref)} title="Chegou atrasado (sem os +5)"
                            className={`text-[9px] uppercase tracking-widest font-bold px-2 py-2 border-l border-[#222] shrink-0 ${isLate ? "text-[#EAB308]" : "text-[#555] hover:text-white"}`}
                            data-testid={`attendance-late-${p.ref}`}>
                            {isLate ? "Atrasado" : "Pontual"}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {isOpen && (
              <div className="border-t border-[#161616] p-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4"
                data-testid={`convocation-detail-${c.id}`}>
                {(c.by_club || []).map((row) => (
                  <div key={row.club} className="border border-[#222]">
                    <div className="p-3 border-b border-[#222] flex items-center gap-2">
                      <span>{CLUB_EMOJI[row.club] || "•"}</span>
                      <span className="font-display font-black uppercase text-sm flex-1">{row.club}</span>
                      <span className="text-[10px] text-[#E50000] font-bold">{row.time}</span>
                    </div>
                    <div className="px-3 py-1.5 text-[9px] uppercase tracking-widest border-b border-[#161616] flex gap-2">
                      <span className="text-[#22C55E]">{row.yes} sim</span>
                      <span className="text-[#EAB308]">{row.maybe} talvez</span>
                      <span className="text-[#737373]">{row.no} não</span>
                      <span className="text-[#404040]">{row.pending} s/ resposta</span>
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      {row.people.map((p) => (
                        <div key={p.email || p.name} className="px-3 py-1.5 border-b border-[#111] last:border-0 flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full shrink-0"
                            style={{ background: ANSWER[p.answer || "pending"].color }} />
                          <span className="text-xs flex-1 truncate">{p.name}</span>
                          <span className="text-[9px] uppercase tracking-widest shrink-0"
                            style={{ color: ANSWER[p.answer || "pending"].color }}>
                            {ANSWER[p.answer || "pending"].label}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                <div className="md:col-span-2 xl:col-span-4 text-[10px] uppercase tracking-widest text-[#737373] flex items-center gap-2">
                  <Users className="w-3 h-3" /> {c.recipients_count || 0} destinatários no último envio
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
